/**
 * V76 — multisucursal Fase 1 (frontend):
 *   - <BranchSelector/> (Topbar): cambia la sucursal ACTIVA de la sesión.
 *     Llama POST /auth/active-branch, guarda el access token nuevo (el refresh
 *     no cambia) y recarga para que TODO el contexto (mesas, comanda, corte,
 *     reportes) quede scoped a la sucursal elegida. Aparece si el tenant tiene
 *     más de una sucursal activa Y algún módulo branch-scoped
 *     (gastro/boutique/gym) — vertical-agnóstico, fix del smoke V76.
 *     "Todas" = vista consolidada.
 *     V76-fix (Opción A): un usuario CON sucursal base está CANDADO a ella —
 *     ve su sucursal como chip fijo (sin "Todas", sin switch; la API además
 *     rechaza el switch con 403). La cobertura la hace un admin reasignando
 *     su sucursal base en Configuración → Usuarios.
 *   - <BranchesSection/> (Configuración): CRUD de sucursales (branches:manage).
 *     Siempre hay UNA default (backfill V76); la default no se desactiva.
 *
 * V81 — guardrails branches↔billing (diseño aprobado 2026-07-09):
 *   - La principal es de PRIMERA CLASE: edición inline + chip "Principal / base".
 *   - Gate de primera expansión: si la base no está confirmada, "Crear sucursal"
 *     abre primero el paso "Nombra tu sucursal principal" (PUT confirmBase).
 *   - Con billing vivo (me.billing.checkoutEnabled): crear/desactivar/reactivar
 *     pasan por un modal de confirmación con el monto EXACTO del +30%
 *     (GET /branches/billing-impact) y mandan confirmBilling=true. El backend
 *     además lo EXIGE (409) — la UI es cortesía, el enforcement es del server.
 */
import { useEffect, useState } from 'react';
import { api, setToken } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { getStripe } from '../lib/stripe';
import { PaymentMethodForm } from './PaymentMethodForm';

export type BranchRow = {
  id: string; name: string; address: string | null; phone: string | null;
  isDefault: boolean; isActive: boolean; sortOrder: number;
  baseConfirmedAt?: string | null; deactivatedAt?: string | null;
};

type BillingImpact = {
  applies: boolean; subscribed: boolean; manualPaidUntil: string | null;
  interval: 'monthly' | 'yearly'; activeBranches: number;
  branchUnitAmount: number | null; immediateAmount: number | null;
  currency: string | null;
  maxBranches: number; reactivationCooldownDays: number;
};

/** Contexto del reintento tras un fallo de cobro (recuperación / SCA). */
type RetryCtx =
  | { type: 'create' }
  | { type: 'toggle'; b: BranchRow; to: boolean };

type ConfirmStep =
  | { kind: 'base' }
  | { kind: 'create' }
  | { kind: 'toggle'; b: BranchRow; to: boolean }
  | { kind: 'recover'; retry: RetryCtx };

const inputCls = 'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm';
const btnGhost = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';
const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const errMsg = (e: any, fb: string) => e?.body?.message || e?.message || fb;

const fmtMoney = (cents: number, currency: string | null) =>
  (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: (currency || 'MXN').toUpperCase() });

/** "+30% ($540.00 /mes)" con monto real de Stripe; sin monto queda "+30%". */
const plusCopy = (impact: BillingImpact | null) =>
  impact?.branchUnitAmount
    ? `+30% (${fmtMoney(impact.branchUnitAmount, impact.currency)} ${impact.interval === 'yearly' ? '/año' : '/mes'})`
    : '+30%';

/** Copy del cargo al agregar/reactivar: con suscripción de Stripe se cobra el
 *  prorrateo AHORA (cobro inmediato 2026-07-09 — cierra la fuga anual); sin
 *  monto inmediato (pago manual / preview caído) cae al copy genérico. */
const chargeCopy = (impact: BillingImpact | null, verb: string) =>
  impact?.immediateAmount != null ? (
    <>
      {verb} <span className="font-semibold text-slate-900">se cobra {fmtMoney(impact.immediateAmount, impact.currency)} AHORA</span>{' '}
      por lo que resta de tu periodo. A partir de la renovación, tu suscripción incluye{' '}
      <span className="font-medium text-slate-900">{plusCopy(impact)}</span> por esta sucursal.
    </>
  ) : (
    <>
      {verb} agrega <span className="font-medium text-slate-900">{plusCopy(impact)}</span> a tu
      suscripción, prorrateado en tu próxima factura.
    </>
  );

// Módulos que operan por sucursal (ventas/mesas/corte/reportes). CRM puro no
// tiene ubicación física → sin selector. Mismo fallback de compat que usa el
// Dashboard cuando /me aún no trae enabledModules.
const BRANCH_SCOPED_MODULES = ['gastro', 'boutique', 'gym'];

// ---------------------------------------------------------------------------
export function BranchSelector() {
  const { user } = useAuth() as any;
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [busy, setBusy] = useState(false);

  // Vertical-agnóstico (fix smoke V76): renderiza en CUALQUIER vertical que
  // venda/corte por sucursal (GastroGest, Boutique y Wellness por igual).
  // Ajuste 1a: Flow/CRM puro NO tiene sucursales — se decide ANTES de fetchear
  // (la API además responde 403 vía @RequiresModule, doble enforcement).
  const enabledModules: string[] = user?.tenant?.enabledModules?.length
    ? user.tenant.enabledModules
    : ['crm', 'boutique', 'gastro'];
  const branchScoped = BRANCH_SCOPED_MODULES.some((m) => enabledModules.includes(m));

  useEffect(() => {
    if (!branchScoped) return;
    api<BranchRow[]>('/branches').then((r) => setBranches(r.filter((b) => b.isActive)))
      .catch(() => setBranches([]));
  }, [branchScoped]);

  if (!branchScoped) return null;
  if (branches.length <= 1) return null; // una sola sucursal: nada que elegir

  // V76-fix (Opción A): usuario con sucursal base = candado. Chip fijo con SU
  // sucursal — sin "Todas (consolidado)" ni switch (la API también lo rechaza).
  const home = user?.branch?.homeBranchId ?? null;
  if (home) {
    const homeName = branches.find((b) => b.id === home)?.name ?? 'Mi sucursal';
    return (
      <span
        className="rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs font-medium text-slate-600"
        title="Tu usuario está asignado a esta sucursal; un administrador puede reasignarte">
        {homeName}
      </span>
    );
  }

  const active = user?.branch?.activeBranchId ?? '';
  const switchTo = async (branchId: string) => {
    setBusy(true);
    try {
      const r = await api<{ accessToken: string }>('/auth/active-branch', {
        method: 'POST', body: { branchId: branchId || null },
      });
      setToken(r.accessToken);
      window.location.reload(); // recarga el contexto completo con la sucursal nueva
    } catch { setBusy(false); }
  };

  return (
    <select
      className="min-w-0 max-w-[8.5rem] shrink rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 sm:max-w-[16rem]"
      value={active} disabled={busy} title="Sucursal activa de tu sesión"
      onChange={(e) => switchTo(e.target.value)}>
      <option value="">Todas (consolidado)</option>
      {branches.map((b) => (
        <option key={b.id} value={b.id}>{b.name}{b.isDefault ? ' ★' : ''}</option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
export function BranchesSection() {
  const { user } = useAuth() as any;
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Edición inline (la principal es una sucursal de primera clase: se renombra
  // y se le pone dirección/teléfono igual que a cualquier otra).
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState({ name: '', address: '', phone: '' });
  // V81 — pasos de confirmación (base / crear / activar-desactivar).
  const [confirmStep, setConfirmStep] = useState<ConfirmStep | null>(null);
  const [impact, setImpact] = useState<BillingImpact | null>(null);
  const [base, setBase] = useState({ name: '', address: '', phone: '' });

  // Kill-switch server-side (FAIL-CLOSED): sin billing vivo, el flujo queda como
  // V76 (sin modales de cobro) — el backend tampoco exige confirmBilling.
  const billingOn = !!user?.billing?.checkoutEnabled;

  // Ajuste 1a: sucursales solo para verticales con POS físico (gastro/boutique/
  // gym). Flow/CRM puro no ve la sección (la API además 403ea el CRUD).
  const enabledModules: string[] = user?.tenant?.enabledModules?.length
    ? user.tenant.enabledModules
    : ['crm', 'boutique', 'gastro'];
  const branchScoped = BRANCH_SCOPED_MODULES.some((m) => enabledModules.includes(m));

  const load = () => api<BranchRow[]>('/branches').then(setBranches).catch(() => setBranches([]));
  useEffect(() => { if (branchScoped) load(); }, [branchScoped]);

  if (!branchScoped) return null;

  const loadImpact = async () => {
    try { setImpact(await api<BillingImpact>('/branches/billing-impact')); }
    catch { setImpact(null); } // sin monto: los modales muestran "+30%" genérico
  };

  const closeConfirm = () => { setConfirmStep(null); setErr(null); };

  const doCreate = async (confirmBilling: boolean, pendingInvoiceId?: string) => {
    setBusy(true); setErr(null);
    try {
      await api('/branches', {
        method: 'POST',
        body: {
          name: name.trim(),
          ...(address.trim() && { address: address.trim() }),
          ...(phone.trim() && { phone: phone.trim() }),
          ...(confirmBilling && { confirmBilling: true }),
          ...(pendingInvoiceId && { pendingInvoiceId }),
        },
      });
      setName(''); setAddress(''); setPhone('');
      setConfirmStep(null);
      await load();
    } catch (e: any) { dispatchPaymentError(e, { type: 'create' }, 'No se pudo crear'); }
    finally { setBusy(false); }
  };

  // ---- Recuperación de pago (rechazo / 3DS step-up) -------------------------
  /** Fallo de cobro → SCA inline (handleNextAction) o paso de recuperación
   *  (cambiar tarjeta + reintentar). Cualquier otro error → mensaje normal. */
  const dispatchPaymentError = (e: any, retry: RetryCtx, fallback: string) => {
    const code = e?.body?.code;
    if (code === 'SCA_REQUIRED' && e?.body?.clientSecret && e?.body?.invoiceId) {
      void handleSca(e.body.clientSecret, e.body.invoiceId, retry);
      return;
    }
    if (code === 'PAYMENT_FAILED') {
      setConfirmStep({ kind: 'recover', retry });
      setErr(e?.body?.message ?? fallback);
      return;
    }
    setErr(errMsg(e, fallback));
  };

  /** Step-up 3DS EN el pago: completa el PaymentIntent inline y reintenta con
   *  pendingInvoiceId (el server VERIFICA la misma factura — sin doble cobro).
   *  Cancelado/fallado → abort best-effort (void + revert) y paso de recuperación. */
  const handleSca = async (clientSecret: string, invoiceId: string, retry: RetryCtx) => {
    setErr(null);
    const abortPending = () =>
      api('/branches/abort-charge', { method: 'POST', body: { invoiceId } }).catch(() => { /* sweep/cron respaldan */ });
    const stripe = await getStripe();
    if (!stripe) {
      await abortPending();
      setConfirmStep({ kind: 'recover', retry });
      setErr('Tu banco requiere autenticación adicional, pero Stripe.js no está disponible en este ambiente.');
      return;
    }
    const res = await stripe.handleNextAction({ clientSecret });
    const ok = !res.error && res.paymentIntent && ['succeeded', 'processing'].includes(res.paymentIntent.status);
    if (!ok) {
      await abortPending();
      setConfirmStep({ kind: 'recover', retry });
      setErr(res.error?.message ?? 'La autenticación no se completó. Puedes intentar con otra tarjeta.');
      return;
    }
    if (retry.type === 'create') await doCreate(true, invoiceId);
    else await attemptToggle(retry.b, retry.to, invoiceId);
  };

  /** Reintento desde el paso de recuperación: intento FRESCO (la factura del
   *  fallo anterior ya quedó anulada por el rollback/sweep del server). */
  const retryFromRecovery = async () => {
    if (confirmStep?.kind !== 'recover') return;
    if (confirmStep.retry.type === 'create') await doCreate(true);
    else await attemptToggle(confirmStep.retry.b, confirmStep.retry.to);
  };

  const openPortal = async () => {
    try {
      const r = await api<{ url: string }>('/billing/portal-session', { method: 'POST', body: {} });
      window.open(r.url, '_blank', 'noopener');
    } catch (e: any) { setErr(errMsg(e, 'No se pudo abrir el portal')); }
  };

  /** Entrada del flujo de creación: gate de base → confirmación de cobro → POST. */
  const openCreateFlow = () => {
    if (!name.trim()) { setErr('Falta el nombre'); return; }
    setErr(null);
    const def = branches.find((b) => b.isDefault);
    if (def && !def.baseConfirmedAt) {
      setBase({ name: def.name, address: def.address ?? '', phone: def.phone ?? '' });
      setConfirmStep({ kind: 'base' });
      return;
    }
    if (billingOn) { void loadImpact(); setConfirmStep({ kind: 'create' }); return; }
    void doCreate(false);
  };

  /** Paso "Nombra tu sucursal principal" (gate de primera expansión, V81). */
  const confirmBase = async () => {
    const def = branches.find((b) => b.isDefault);
    if (!def) return;
    if (!base.name.trim()) { setErr('Falta el nombre de la sucursal principal'); return; }
    setBusy(true); setErr(null);
    try {
      await api(`/branches/${def.id}`, {
        method: 'PUT',
        body: {
          name: base.name.trim(), address: base.address.trim(), phone: base.phone.trim(),
          confirmBase: true,
        },
      });
      await load();
      if (billingOn) { void loadImpact(); setConfirmStep({ kind: 'create' }); }
      else { setConfirmStep(null); await doCreate(false); }
    } catch (e: any) { setErr(errMsg(e, 'No se pudo confirmar la sucursal base')); }
    finally { setBusy(false); }
  };

  const patch = async (b: BranchRow, body: Record<string, unknown>) => {
    setErr(null);
    try { await api(`/branches/${b.id}`, { method: 'PUT', body }); await load(); return true; }
    catch (e: any) { setErr(errMsg(e, 'No se pudo actualizar')); return false; }
  };

  /** Activar/desactivar: con billing vivo pasa por el modal de confirmación. */
  const openToggleFlow = (b: BranchRow) => {
    if (!billingOn) { void patch(b, { isActive: !b.isActive }); return; }
    setErr(null);
    void loadImpact();
    setConfirmStep({ kind: 'toggle', b, to: !b.isActive });
  };

  /** Activar/desactivar con manejo de recuperación (rechazo → recover; SCA →
   *  handleNextAction inline; 429 cooldown u otros → error en el modal). */
  const attemptToggle = async (b: BranchRow, to: boolean, pendingInvoiceId?: string) => {
    setBusy(true); setErr(null);
    try {
      await api(`/branches/${b.id}`, {
        method: 'PUT',
        body: { isActive: to, confirmBilling: true, ...(pendingInvoiceId && { pendingInvoiceId }) },
      });
      setConfirmStep(null);
      await load();
    } catch (e: any) { dispatchPaymentError(e, { type: 'toggle', b, to }, 'No se pudo actualizar'); }
    finally { setBusy(false); }
  };

  const doToggle = async () => {
    if (confirmStep?.kind !== 'toggle') return;
    await attemptToggle(confirmStep.b, confirmStep.to);
  };

  const startEdit = (b: BranchRow) => {
    setErr(null);
    setEditing(b.id);
    setEdit({ name: b.name, address: b.address ?? '', phone: b.phone ?? '' });
  };
  const saveEdit = async (b: BranchRow) => {
    if (!edit.name.trim()) { setErr('Falta el nombre'); return; }
    setErr(null);
    try {
      await api(`/branches/${b.id}`, {
        method: 'PUT',
        body: { name: edit.name.trim(), address: edit.address.trim(), phone: edit.phone.trim() },
      });
      setEditing(null);
      await load();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo actualizar')); }
  };

  const historyCopy =
    'Toda la historia previa — y cualquier venta hecha sin sucursal elegida — queda asignada a la sucursal base. La sucursal nueva empieza desde cero.';

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Sucursales</h3>
        <p className="text-sm text-slate-500">
          Ventas, mesas y cortes de caja se separan por sucursal; los reportes se ven por sucursal o consolidados.
          La sucursal ★ es la principal (recibe la historia previa y las ventas sin sucursal elegida).
        </p>
      </div>
      <div className="space-y-3 px-6 py-5 md:px-8">
        {branches.map((b) => (
          <div key={b.id} className={`rounded-xl border border-slate-100 px-4 py-2.5 text-sm ${b.isActive ? '' : 'opacity-50'}`}>
            {editing === b.id ? (
              <div className="space-y-2">
                <input className={inputCls} placeholder="Nombre (ej. Sucursal Centro)" value={edit.name}
                  onChange={(e) => setEdit((s) => ({ ...s, name: e.target.value }))} maxLength={150} />
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputCls} placeholder="Dirección (sale en el ticket)" value={edit.address}
                    onChange={(e) => setEdit((s) => ({ ...s, address: e.target.value }))} maxLength={500} />
                  <input className={inputCls} placeholder="Teléfono" value={edit.phone}
                    onChange={(e) => setEdit((s) => ({ ...s, phone: e.target.value }))} maxLength={50} />
                </div>
                <div className="flex justify-end gap-1.5">
                  <button className={btnGhost} onClick={() => setEditing(null)}>Cancelar</button>
                  <button className={btnGhost} onClick={() => saveEdit(b)}>Guardar</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 truncate font-medium text-slate-700">
                    {b.name}
                    {b.isDefault && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700"
                        title="Recibe la historia previa y las ventas sin sucursal elegida">
                        Principal / base
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-[11px] text-slate-400">
                    {[b.address, b.phone].filter(Boolean).join(' · ') || 'Sin dirección'}
                  </span>
                </span>
                <span className="flex items-center gap-1.5">
                  <button className={btnGhost} onClick={() => startEdit(b)}>Editar</button>
                  {!b.isDefault && b.isActive && (
                    <button className={btnGhost} onClick={() => patch(b, { isDefault: true })}>Hacer principal</button>
                  )}
                  {!b.isDefault && (
                    <button className={btnGhost} onClick={() => openToggleFlow(b)}>
                      {b.isActive ? 'Desactivar' : 'Activar'}
                    </button>
                  )}
                </span>
              </div>
            )}
          </div>
        ))}

        <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-4">
          <p className="text-[12px] font-medium text-slate-500">+ Nueva sucursal</p>
          <input className={inputCls} placeholder="Nombre (ej. Sucursal Centro)" value={name} onChange={(e) => setName(e.target.value)} maxLength={150} />
          <div className="grid grid-cols-2 gap-2">
            <input className={inputCls} placeholder="Dirección (sale en el ticket)" value={address} onChange={(e) => setAddress(e.target.value)} maxLength={500} />
            <input className={inputCls} placeholder="Teléfono" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={50} />
          </div>
          {billingOn && (
            <p className="text-[11px] text-slate-400">
              Cada sucursal adicional agrega +30% a tu suscripción; el resto del periodo actual se
              cobra al momento. Te pediremos confirmación con el monto exacto.
            </p>
          )}
          {err && !confirmStep && <p className="text-sm text-rose-600">{err}</p>}
          <div className="flex justify-end">
            <button className={btnPrimary} onClick={openCreateFlow} disabled={busy}>Crear sucursal</button>
          </div>
        </div>
      </div>

      {/* ---- Modales de confirmación (V81) ---------------------------------- */}
      {confirmStep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          {/* max-h + overflow: con el teclado abierto en teléfono, el Card Element de
              Stripe (variante 'recover') crece y empujaba "Reintentar cobro" fuera de
              pantalla. Si el cajero no ve ese botón, cobra por otra vía. */}
          <div className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            {confirmStep.kind === 'base' && (
              <>
                <h4 className="font-serif text-lg text-slate-900">Nombra tu sucursal principal</h4>
                <p className="text-sm text-slate-600">{historyCopy}</p>
                <input className={inputCls} placeholder="Nombre de la sucursal base" value={base.name}
                  onChange={(e) => setBase((s) => ({ ...s, name: e.target.value }))} maxLength={150} />
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputCls} placeholder="Dirección (opcional)" value={base.address}
                    onChange={(e) => setBase((s) => ({ ...s, address: e.target.value }))} maxLength={500} />
                  <input className={inputCls} placeholder="Teléfono (opcional)" value={base.phone}
                    onChange={(e) => setBase((s) => ({ ...s, phone: e.target.value }))} maxLength={50} />
                </div>
                {err && <p className="text-sm text-rose-600">{err}</p>}
                <div className="flex justify-end gap-2">
                  <button className={btnGhost} onClick={closeConfirm} disabled={busy}>Cancelar</button>
                  <button className={btnPrimary} onClick={confirmBase} disabled={busy}>Confirmar sucursal base</button>
                </div>
              </>
            )}

            {confirmStep.kind === 'create' && (
              <>
                <h4 className="font-serif text-lg text-slate-900">Sucursal adicional</h4>
                {impact && !impact.subscribed ? (
                  <>
                    <p className="text-sm text-slate-600">
                      Las sucursales adicionales requieren una suscripción activa. Suscríbete y vuelve a intentarlo.
                    </p>
                    <div className="flex justify-end">
                      <button className={btnGhost} onClick={closeConfirm}>Entendido</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-600">
                      {chargeCopy(impact, `Al crear "${name.trim()}"`)}
                    </p>
                    <p className="text-[12px] text-slate-500">{historyCopy}</p>
                    {err && <p className="text-sm text-rose-600">{err}</p>}
                    <div className="flex justify-end gap-2">
                      <button className={btnGhost} onClick={closeConfirm} disabled={busy}>Cancelar</button>
                      <button className={btnPrimary} onClick={() => doCreate(true)} disabled={busy}>Confirmar y crear</button>
                    </div>
                  </>
                )}
              </>
            )}

            {confirmStep.kind === 'recover' && (
              <>
                <h4 className="font-serif text-lg text-slate-900">No pudimos procesar el cobro</h4>
                <p className="text-sm text-slate-600">
                  Tu tarjeta guardada no pudo ser cobrada. Actualiza tu método de pago y reintenta —
                  sin salir del app.
                </p>
                {(user?.role === 'SUPER_USER') ? (
                  <PaymentMethodForm onSaved={() => setErr(null)} />
                ) : (
                  <p className="text-sm text-slate-500">
                    Pide al administrador de tu cuenta actualizar el método de pago e inténtalo de nuevo.
                  </p>
                )}
                {err && <p className="text-sm text-rose-600">{err}</p>}
                <div className="flex items-center justify-between gap-2">
                  {user?.role === 'SUPER_USER' ? (
                    <button onClick={openPortal} className="text-xs text-slate-400 underline hover:text-slate-600">
                      o gestiona tus métodos de pago en el portal de Stripe
                    </button>
                  ) : <span />}
                  <span className="flex gap-2">
                    <button className={btnGhost} onClick={closeConfirm} disabled={busy}>Cancelar</button>
                    <button className={btnPrimary} onClick={retryFromRecovery} disabled={busy}>
                      {busy ? 'Procesando…' : 'Reintentar cobro'}
                    </button>
                  </span>
                </div>
              </>
            )}

            {confirmStep.kind === 'toggle' && (
              <>
                <h4 className="font-serif text-lg text-slate-900">
                  {confirmStep.to ? 'Reactivar sucursal' : 'Desactivar sucursal'}
                </h4>
                <p className="text-sm text-slate-600">
                  {confirmStep.to ? (
                    chargeCopy(impact, `Al reactivar "${confirmStep.b.name}"`)
                  ) : (
                    <>Al desactivar <span className="font-medium text-slate-900">{confirmStep.b.name}</span> recibirás
                      un <span className="font-medium text-slate-900">crédito en tu balance</span> por el tiempo no
                      usado, que se aplicará a tu próxima factura (no es un reembolso en efectivo). Una sucursal
                      desactivada no puede reactivarse durante {impact?.reactivationCooldownDays ?? 7} días.</>
                  )}
                </p>
                {err && <p className="text-sm text-rose-600">{err}</p>}
                <div className="flex justify-end gap-2">
                  <button className={btnGhost} onClick={closeConfirm} disabled={busy}>Cancelar</button>
                  <button className={btnPrimary} onClick={doToggle} disabled={busy}>
                    {confirmStep.to ? 'Confirmar y reactivar' : 'Confirmar y desactivar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
